/**
 * MOX · Ajustes de interfaz globales.
 * - WhatsApp (botón de app): lo sube sobre la barra de compra móvil y lo oculta
 *   mientras hay un panel abierto (carrito, menú, búsqueda). No cambia su destino.
 * - Aviso de recálculo de kit en el carrito.
 * - Sección activa en la navegación principal.
 */
import { ThemeEvents } from '@theme/events';
import { takeNotice } from '@theme/mox-cart';

/* ---------- WhatsApp ---------- */

function findWhatsAppButton() {
  const links = document.querySelectorAll(
    '[class*="wa-preview"], [class*="wa-widget"], a[href*="wa.me"], a[href*="whatsapp.com"], [class*="whatsapp" i], [id*="whatsapp" i]'
  );
  for (const node of links) {
    let el = /** @type {HTMLElement | null} */ (node);
    while (el && el !== document.body) {
      const style = getComputedStyle(el);
      if (style.position === 'fixed') return el;
      el = el.parentElement;
    }
  }
  return null;
}

function manageWhatsApp() {
  if (!document.body.classList.contains('mox-wa-managed')) return;
  let tries = 0;
  const mark = () => {
    const target = findWhatsAppButton();
    if (target) {
      target.classList.add('mox-wa-target');
      return true;
    }
    return false;
  };
  if (mark()) return;
  const observer = new MutationObserver(() => {
    tries += 1;
    if (mark() || tries > 200) observer.disconnect();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 15000);
}

/* ---------- Paneles abiertos ---------- */

function isShown(el) {
  const rect = el.getBoundingClientRect();
  const style = getComputedStyle(el);
  return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
}

function syncPanelState() {
  // Cajón del carrito, búsqueda y menú móvil: solo cuentan si realmente están visibles.
  const candidates = document.querySelectorAll('dialog[open], header-drawer details[open] .menu-drawer, details.menu-drawer-container[open] .menu-drawer');
  const open = [...candidates].some(isShown);
  document.body.classList.toggle('mox-panel-open', open);
}

function watchPanels() {
  const observer = new MutationObserver(syncPanelState);
  observer.observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });
  syncPanelState();
}

/* ---------- Aviso de kit ---------- */

function showKitNotice(message) {
  const text = message || takeNotice();
  if (!text) return;
  document.querySelectorAll('[data-mox-kit-notice]').forEach((el) => {
    el.textContent = text;
    /** @type {HTMLElement} */ (el).hidden = false;
  });
}

/* ---------- Navegación activa ---------- */

function markActiveNav() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const isProduct = document.querySelector('main[data-template^="product"]');
  const links = document.querySelectorAll('header-component a[href], header-drawer a[href], .menu-drawer a[href]');
  links.forEach((link) => {
    const a = /** @type {HTMLAnchorElement} */ (link);
    if (a.origin !== window.location.origin) return;
    const href = a.pathname.replace(/\/$/, '') || '/';
    if (href === '/') return;
    const matches = href === path || (isProduct && href.endsWith('/collections/all'));
    if (matches && !a.hasAttribute('aria-current')) a.setAttribute('aria-current', 'page');
  });
}

/**
 * Envío estimado: reemplaza [desde] y [hasta] por fechas reales contando días
 * hábiles (lunes a viernes) desde hoy. Sin JavaScript queda el plazo en días.
 */
function addBusinessDays(from, days) {
  const date = new Date(from);
  let left = days;
  while (left > 0) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  return date;
}

function renderShippingEta() {
  const format = new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'short' });
  const today = new Date();
  document.querySelectorAll('[data-mox-eta-note]').forEach((el) => {
    const node = /** @type {HTMLElement} */ (el);
    const min = Number(node.dataset.min) || 0;
    const max = Math.max(Number(node.dataset.max) || 0, min);
    const from = format.format(addBusinessDays(today, min)).replace('.', '');
    const to = format.format(addBusinessDays(today, max)).replace('.', '');
    const template = node.dataset.moxEtaNote || '';
    const text = min === max ? template.replace(/[desde]s*-s*[hasta]/, from) : template;
    node.textContent = text.replace('[desde]', from).replace('[hasta]', to);
  });
}

/**
 * Carruseles simples ([data-mox-carousel] con [data-mox-track] y botones
 * [data-mox-prev]/[data-mox-next]) y comparador antes/después ([data-mox-ba]).
 * Con delegación de eventos, así siguen andando cuando el editor de Shopify
 * vuelve a dibujar la sección.
 */
document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const nav = target.closest('[data-mox-prev], [data-mox-next]');
  if (nav) {
    const track = nav.closest('[data-mox-carousel]')?.querySelector('[data-mox-track]');
    if (!track) return;
    const item = track.firstElementChild;
    const step = item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: nav.hasAttribute('data-mox-prev') ? -step : step, behavior: reduce ? 'auto' : 'smooth' });
    return;
  }
  const tab = target.closest('.mox-ba__tab');
  if (tab) selectBeforeAfterTab(/** @type {HTMLElement} */ (tab));
});

function selectBeforeAfterTab(tab) {
  const root = tab.closest('[data-mox-ba]');
  if (!root) return;
  root.querySelectorAll('.mox-ba__tab').forEach((el) => {
    const selected = el === tab;
    el.setAttribute('aria-selected', String(selected));
    el.setAttribute('tabindex', selected ? '0' : '-1');
    const panel = root.querySelector('#' + el.getAttribute('aria-controls'));
    if (panel) /** @type {HTMLElement} */ (panel).hidden = !selected;
  });
}

document.addEventListener('keydown', (event) => {
  const tab = /** @type {HTMLElement} */ (event.target).closest?.('.mox-ba__tab');
  if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const tabs = [...tab.parentElement.querySelectorAll('.mox-ba__tab')];
  const i = tabs.indexOf(tab);
  const next =
    event.key === 'Home' ? tabs[0]
    : event.key === 'End' ? tabs[tabs.length - 1]
    : tabs[(i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
  event.preventDefault();
  /** @type {HTMLElement} */ (next).focus();
  selectBeforeAfterTab(next);
});

document.addEventListener('input', (event) => {
  const range = /** @type {HTMLInputElement} */ (event.target);
  if (!range.matches?.('[data-mox-ba-range]')) return;
  range.closest('.mox-ba__frame')?.style.setProperty('--mox-ba-pos', range.value + '%');
});

function start() {
  manageWhatsApp();
  watchPanels();
  markActiveNav();
  showKitNotice();
  renderShippingEta();
}

document.addEventListener('mox:kit-notice', (event) => {
  // Se muestra después de que el tema reemplaza el carrito.
  requestAnimationFrame(() => requestAnimationFrame(() => showKitNotice(/** @type {CustomEvent} */ (event).detail.message)));
});

document.addEventListener(ThemeEvents.cartUpdate, () => {
  requestAnimationFrame(() => requestAnimationFrame(() => showKitNotice()));
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
